import * as yup from "yup"

/**
 * @param {string} cValue
 */
const validateCPF = (cValue) => {
  const cCPF = cValue.replace(/\D/g, "")
  if (cCPF.length !== 11) return false

  // Elimina CPFs invalidos conhecidos
  if (
    cCPF === "00000000000" ||
    cCPF === "11111111111" ||
    cCPF === "22222222222" ||
    cCPF === "33333333333" ||
    cCPF === "44444444444" ||
    cCPF === "55555555555" ||
    cCPF === "66666666666" ||
    cCPF === "77777777777" ||
    cCPF === "88888888888" ||
    cCPF === "99999999999"
  )
    return false

  // Valida 1o digito
  let nAdd = 0
  for (let i = 0; i < 9; i++) {
    nAdd += parseInt(cCPF.charAt(i)) * (10 - i)
  }
  let nRev = 11 - (nAdd % 11)

  if (nRev === 10 || nRev === 11) {
    nRev = 0
  }
  if (nRev !== parseInt(cCPF.charAt(9))) return false

  // Valida 2o digito
  nAdd = 0
  for (let i = 0; i < 10; i++) {
    nAdd += parseInt(cCPF.charAt(i)) * (11 - i)
  }
  nRev = 11 - (nAdd % 11)
  if (nRev === 10 || nRev === 11) {
    nRev = 0
  }
  if (nRev !== parseInt(cCPF.charAt(10))) return false
  return true
}

/**
 * @param {string} cValue
 * @return {boolean}
 */
const validateCNPJ = (cValue) => {
  const cCNPJ = cValue.replace(/\D/g, "")

  if (cCNPJ.length !== 14) return false

  // Elimina CNPJs invalidos conhecidos
  if (
    cCNPJ === "00000000000000" ||
    cCNPJ === "11111111111111" ||
    cCNPJ === "22222222222222" ||
    cCNPJ === "33333333333333" ||
    cCNPJ === "44444444444444" ||
    cCNPJ === "55555555555555" ||
    cCNPJ === "66666666666666" ||
    cCNPJ === "77777777777777" ||
    cCNPJ === "88888888888888" ||
    cCNPJ === "99999999999999"
  )
    return false

  // Valida DVs
  let nSize = cCNPJ.length - 2
  let cNumbers = cCNPJ.substring(0, nSize)
  const cDigits = cCNPJ.substring(nSize)
  let nSum = 0
  let nPos = nSize - 7
  for (let i = nSize; i >= 1; i--) {
    nSum += cNumbers.charAt(nSize - i) * nPos--
    if (nPos < 2) {
      nPos = 9
    }
  }
  let nResult = nSum % 11 < 2 ? 0 : 11 - (nSum % 11)

  if (String(nResult) !== cDigits.charAt(0)) return false

  nSize += 1
  cNumbers = cCNPJ.substring(0, nSize)
  nSum = 0
  nPos = nSize - 7
  for (let i = nSize; i >= 1; i--) {
    nSum += cNumbers.charAt(nSize - i) * nPos--
    if (nPos < 2) {
      nPos = 9
    }
  }
  nResult = nSum % 11 < 2 ? 0 : 11 - (nSum % 11)
  if (String(nResult) !== cDigits.charAt(1)) return false

  return true
}

const oBrazilStates = {
  AC: "Acre",
  AL: "Alagoas",
  AP: "Amapá",
  AM: "Amazonas",
  BA: "Bahia",
  CE: "Ceará",
  DF: "Distrito Federal",
  ES: "Espírito Santo",
  GO: "Goiás",
  MA: "Maranhão",
  MT: "Mato Grosso",
  MS: "Mato Grosso do Sul",
  MG: "Minas Gerais",
  PA: "Pará",
  PB: "Paraiba",
  PR: "Paraná",
  PE: "Pernambuco",
  PI: "Piauí",
  RJ: "Rio de Janeiro",
  RN: "Rio Grande do Norte",
  RS: "Rio Grande do Sul",
  RO: "Rondônia",
  RR: "Roraima",
  SC: "Santa Catarina",
  SP: "São Paulo",
  SE: "Sergipe",
  TO: "Tocantins",
}

export const aBrazilStateOptions = Object.entries(oBrazilStates).map(([cKey, cValue]) => {
  return {
    label: cValue,
    value: cKey,
  }
})

// quando exporta o objeto diretamente, intl não funciona e as mensagens ficam vazias
export const schema = (intl) => {
  const fieldRequired = intl.get("fields.required_field")
  return yup.object().shape({
    cust_ctype: yup
      .string()
      .matches(/^(PF|PJ)$/)
      .required(fieldRequired),
    cust_cname: yup.string().required(fieldRequired),

    cust_ccpf: yup.string().when("cust_ctype", {
      is: "PF",
      then: yup
        .string()
        .required(fieldRequired)
        .test("test-cpf", intl.get("plan.errors.invalid_cpf"), validateCPF),
      otherwise: yup.string().nullable(),
    }),

    cust_ccnpj: yup.string().when("cust_ctype", {
      is: "PJ",
      then: yup
        .string()
        .required(fieldRequired)
        .test("test-cnpj", intl.get("plan.errors.invalid_cnpj"), validateCNPJ),
      otherwise: yup.string().nullable(),
    }),
    cust_cfantasyname: yup.string().when("cust_ctype", {
      is: "PJ",
      then: yup.string().required(fieldRequired),
      otherwise: yup.string().nullable(),
    }),
    cust_cIE: yup.string().when("cust_ctype", {
      is: "PJ",
      then: yup
        .string()
        .min(5, intl.get("plan.errors.invalid_ie"))
        .required(fieldRequired),
      otherwise: yup.string().nullable(),
    }),
    cust_cIM: yup.string().when("cust_ctype", {
      is: "PJ",
      then: yup.string().nullable(),
      otherwise: yup.string().nullable(),
    }),
    ca_bissimple: yup.boolean().when("cust_ctype", {
      is: "PJ",
      then: yup.boolean().required(fieldRequired),
      otherwise: yup.boolean().nullable(),
    }),
    ca_bispublic: yup.boolean().when("cust_ctype", {
      is: "PJ",
      then: yup.boolean().required(fieldRequired),
      otherwise: yup.boolean().nullable(),
    }),

    cust_cpostalcode: yup
      .string()
      .matches(/^\d{5}-\d{3}$/, intl.get("plan.errors.invalid_postalcode"))
      .required(fieldRequired),
    cust_ccity: yup.string().required(fieldRequired),
    // deve ser uma chave de oBrazilStates
    cust_cstate: yup
      .string()
      .test(
        "test-uf",
        intl.get("plan.errors.invalid_uf"),
        (cValue) => !!oBrazilStates[cValue],
      )
      .required(fieldRequired),
    cust_cstreet: yup.string().required(fieldRequired),
    cust_cdistrict: yup.string().required(fieldRequired),
    cust_cnumber: yup.string().required(fieldRequired),
    cust_ccomplement: yup.string().nullable(),

    cust_cemail: yup
      .string()
      .email(intl.get("plan.errors.invalid_email"))
      .required(fieldRequired),
    cust_cphone: yup
      .string()
      .required(fieldRequired)
      .matches(/^\([0-9]{2}\) [0-9]{4,5}-[0-9]{4}_?$/, {
        message: intl.get("plan.errors.invalid_phone") || "aaaaa",
      }),
  })
}
